/**
 * CommandCateringService — provides catering commands to the command palette.
 *
 * RESPONSIBILITY: Searches vendors for the current location and exposes a
 * "Book a meeting" static action. Registers commands with the CommandRegistryService
 * on construction and re-registers whenever the location changes.
 *
 * SIGNALS: None — this service only produces CommandResult[] for the palette.
 *
 * USED BY: CommandPaletteComponent (via CommandRegistryService)
 *
 * AI CONTEXT: Vendor search is async (loads vendors from CateringApiService),
 * but the "Book a meeting" action is always available regardless of location.
 * The vendor commands are re-registered each time vendors are fetched for a new
 * location. The static "Book a meeting" command is registered once at construction.
 */
import { Injectable, inject, DestroyRef } from '@angular/core';
import { Router } from '@angular/router';
import { CommandRegistryService, Command, CommandGroup } from './command-registry.service';
import { CateringApiService } from './catering-api.service';
import { VendorDetailService } from './vendor-detail.service';
import { LocationStateService } from './location-state.service';
import { MeetingComposerService } from './meeting-composer.service';
import { MeetingPanelFlagService } from './meeting-panel-flag.service';
import { CateringVendor } from '../models/catering.model';

/** Shape returned by searchVendors() for external consumers */
export interface CommandResult {
  id: string;
  label: string;
  description?: string;
  icon?: string;
  category: string;
  action: () => void;
}

@Injectable({ providedIn: 'root' })
export class CommandCateringService {
  private readonly api = inject(CateringApiService);
  private readonly vendorDetail = inject(VendorDetailService);
  private readonly locationState = inject(LocationStateService);
  private readonly registry = inject(CommandRegistryService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly composer = inject(MeetingComposerService);
  private readonly router = inject(Router);
  private readonly panelFlag = inject(MeetingPanelFlagService);

  /** Cleanup function for previously registered vendor commands */
  private vendorCleanup: (() => void) | null = null;

  /** Cached vendors for the current location */
  private cachedVendors: CateringVendor[] = [];
  private cachedLocationId: string | null = null;

  constructor() {
    // Register the static "Book a meeting" command once
    const cleanup = this.registry.register({
      id: 'catering:book-meeting',
      title: 'Book a meeting',
      subtitle: 'Create a new room booking with catering',
      aliases: ['meeting', 'book room', 'catering', 'room booking'],
      icon: 'calendar',
      group: 'actions' as CommandGroup,
      score: 70,
      primary: true,
      execute: () => this.bookMeeting(),
    });

    this.destroyRef.onDestroy(() => {
      cleanup();
      this.unregisterVendorCommands();
    });
  }

  /**
   * "Book a meeting": the meeting panel over the page the palette was opened on (the whole schedule editor,
   * no navigation), or the schedule's create link where the tenant has no panel (`MeetingComposerService`).
   */
  private bookMeeting(): void {
    // Flag off (`schedule-meeting-panel`): main's door, the schedule's create link.
    if (!this.panelFlag.on()) {
      void this.router.navigate(['/schedule'], { queryParams: { create: '1' } });
      return;
    }
    this.composer.open({}, { door: 'palette' });
  }

  /**
   * Search vendors for the current location and return command results.
   * Results can be used directly or are also registered with the command registry.
   */
  async searchVendors(query: string): Promise<CommandResult[]> {
    if (query.length < 2) return [];

    const locationId = this.locationState.selectedLocationId();
    if (!locationId) return [];

    // Fetch vendors if location changed or not yet cached
    if (locationId !== this.cachedLocationId) {
      try {
        this.cachedVendors = await this.api.loadVendors(locationId);
        this.cachedLocationId = locationId;
        this.registerVendorCommands(this.cachedVendors);
      } catch (err) {
        console.warn('[CommandCatering] Failed to load vendors:', err);
        return [];
      }
    }

    return this.cachedVendors
      .filter(v => v.name.toLowerCase().includes(query.toLowerCase()))
      .slice(0, 5)
      .map(v => ({
        id: v._id,
        label: v.name,
        description: v.tags.slice(0, 3).join(', '),
        icon: 'utensils',
        category: 'Vendors',
        action: () => this.vendorDetail.open(v._id),
      }));
  }

  /** Get the static commands (for external consumers) */
  getStaticCommands(): CommandResult[] {
    return [
      {
        id: 'book-meeting',
        label: 'Book a meeting',
        description: 'Create a new room booking with catering',
        icon: 'calendar',
        category: 'Actions',
        action: () => this.bookMeeting(),
      },
    ];
  }

  /**
   * Load vendors for the current location and register them as commands.
   * Called when the palette opens or location changes.
   */
  async refreshVendorCommands(): Promise<void> {
    const locationId = this.locationState.selectedLocationId();
    if (!locationId) return;

    try {
      this.cachedVendors = await this.api.loadVendors(locationId);
      this.cachedLocationId = locationId;
      this.registerVendorCommands(this.cachedVendors);
    } catch (err) {
      console.warn('[CommandCatering] Failed to refresh vendor commands:', err);
    }
  }

  /** Register vendor commands with the central registry */
  private registerVendorCommands(vendors: CateringVendor[]): void {
    this.unregisterVendorCommands();

    const commands: Command[] = vendors.map(v => ({
      id: `vendor:${v._id}`,
      title: v.name,
      subtitle: v.tags.slice(0, 3).join(', ') || v.description || '',
      aliases: [...v.tags, v.vendorType, 'vendor', 'catering', 'food'],
      icon: 'utensils',
      group: 'catering' as CommandGroup,
      score: 40,
      primary: false, // Only appear when user is searching
      execute: () => this.vendorDetail.open(v._id),
    }));

    if (commands.length > 0) {
      this.vendorCleanup = this.registry.registerAll(commands);
    }
  }

  /** Unregister previously registered vendor commands */
  private unregisterVendorCommands(): void {
    if (this.vendorCleanup) {
      this.vendorCleanup();
      this.vendorCleanup = null;
    }
  }
}
